// client/src/pages/PropertyPage.js — one property: gallery, facts, the brokerage identity
// block, MLS attribution and disclaimer, and "Request a showing" / "Ask a question", which
// go to the public lead capture (routing assigns an agent; the CRM sees the lead).
// In production the server has already put the title, description and schema.org
// RealEstateListing into the HTML for search engines (server.js, lib/listingSources/seo).
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { IdentityBlock, Attribution, Disclaimer } from '../components/public/BrokerageIdentity';
import { addressLine, placeLine, STATUS_STYLE, photoUrl, onImgError, portalToken, portalFetch } from '../utils/properties';
import { VirtualStagingBadge } from '../components/shared/VirtualStagingBadge';
import { useListingEngagement, track } from '../utils/engagement';
import { useOwnerListing, OwnerListingBadges, OwnerListingDetails, OwnerInquiryForm } from '../components/public/OwnerListingPanel';
import ShowingBooking from '../components/public/ShowingBooking';

const inputCls = 'mt-1 block w-full rounded-md border border-slate-400 bg-white px-3 py-2 text-slate-900 focus:border-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-600';
const LEASE_TERMS = { per_month: 'per month', per_year: 'per year', per_sf_year: 'per sq ft per year', per_sf_month: 'per sq ft per month' };

const Gallery = ({ l }) => {
    const [i, setI] = useState(0);
    const photos = l.photos && l.photos.length ? l.photos : [{ url: photoUrl(l), caption: null }];
    const cur = photos[Math.min(i, photos.length - 1)];
    const alt = (p, n) => p.alt || p.caption || `Photo ${n + 1} of ${photos.length}, ${addressLine(l)}`;
    return (
        <section aria-label="Photos" className="overflow-hidden rounded-lg bg-slate-900">
            <div className="relative">
                <img src={cur.url} onError={onImgError} alt={alt(cur, i)} className="mx-auto max-h-[32rem] w-full object-contain" />
                {cur.virtually_staged && <VirtualStagingBadge />}
                {photos.length > 1 && (
                    <>
                        <button type="button" onClick={() => setI((i - 1 + photos.length) % photos.length)} aria-label="Previous photo"
                            className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 px-3 py-2 text-xl font-bold text-slate-900 focus:outline-none focus:ring-4 focus:ring-indigo-400">‹</button>
                        <button type="button" onClick={() => setI((i + 1) % photos.length)} aria-label="Next photo"
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 px-3 py-2 text-xl font-bold text-slate-900 focus:outline-none focus:ring-4 focus:ring-indigo-400">›</button>
                        <p className="absolute bottom-2 right-2 rounded bg-black/70 px-2 py-1 text-xs text-white" aria-live="polite">{i + 1} / {photos.length}</p>
                    </>
                )}
            </div>
            {photos.length > 1 && (
                <ul className="flex gap-2 overflow-x-auto bg-slate-800 p-2" aria-label="Choose a photo">
                    {photos.map((p, n) => (
                        <li key={p.url + n}>
                            <button type="button" onClick={() => setI(n)} aria-label={`Show photo ${n + 1}`} aria-current={n === i ? 'true' : undefined}
                                className={`relative block h-16 w-24 overflow-hidden rounded focus:outline-none focus:ring-4 focus:ring-indigo-400 ${n === i ? 'ring-2 ring-white' : ''}`}>
                                <span className="relative block h-full w-full">
                                    <img src={p.url} onError={onImgError} alt="" className="h-full w-full object-cover" loading="lazy" />
                                    {p.virtually_staged && <VirtualStagingBadge className="text-[9px] px-1" />}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
};

const Fact = ({ label, value }) => (value === null || value === undefined || value === '' ? null : (
    <div className="border-b border-slate-200 py-2"><dt className="text-sm text-slate-600">{label}</dt><dd className="font-medium text-slate-900">{value}</dd></div>
));

export const InquiryForm = ({ listingKey, endpoint = '/api/public/lead-capture', extra = {}, defaultType = 'showing', compact = false }) => {
    const [type, setType] = useState(defaultType);
    const [f, setF] = useState({ name: '', email: '', phone: '', message: '', preferred_times: '', website: '' });
    const [state, setState] = useState({ sending: false, done: '', error: '' });
    const startedRef = useRef(false);
    const onStart = () => { if (!startedRef.current) { startedRef.current = true; track('inquiry_started', listingKey); } };
    const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
    const submit = async (e) => {
        e.preventDefault();
        setState({ sending: true, done: '', error: '' });
        try {
            const r = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...f, ...extra, listing_key: listingKey, inquiry_type: type }) });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j.message || 'Could not send. Please call our office.');
            setState({ sending: false, done: j.message || 'Thank you — an agent will be in touch.', error: '' });
        } catch (err) { setState({ sending: false, done: '', error: err.message }); }
    };
    if (state.done) return <p className="rounded-md bg-emerald-50 p-4 text-emerald-900" role="status">{state.done}</p>;
    const id = (k) => `inq-${listingKey}-${k}`;
    return (
        <form onSubmit={submit} onFocus={onStart} className="space-y-3" aria-label={type === 'showing' ? 'Request a showing' : 'Ask a question'}>
            <fieldset>
                <legend className="text-sm font-medium text-slate-800">I would like to</legend>
                <div className="mt-1 flex gap-4">
                    <label className="flex items-center gap-2 text-slate-900"><input type="radio" name={id('type')} checked={type === 'showing'} onChange={() => setType('showing')} /> Request a showing</label>
                    <label className="flex items-center gap-2 text-slate-900"><input type="radio" name={id('type')} checked={type === 'question'} onChange={() => setType('question')} /> Ask a question</label>
                </div>
            </fieldset>
            {!compact && (
                <>
                    <div><label htmlFor={id('name')} className="block text-sm font-medium text-slate-800">Name</label>
                        <input id={id('name')} required autoComplete="name" className={inputCls} value={f.name} onChange={set('name')} /></div>
                    <div><label htmlFor={id('email')} className="block text-sm font-medium text-slate-800">Email</label>
                        <input id={id('email')} type="email" autoComplete="email" className={inputCls} value={f.email} onChange={set('email')} /></div>
                    <div><label htmlFor={id('phone')} className="block text-sm font-medium text-slate-800">Phone</label>
                        <input id={id('phone')} type="tel" autoComplete="tel" className={inputCls} value={f.phone} onChange={set('phone')} /></div>
                </>
            )}
            {type === 'showing' && (
                <div><label htmlFor={id('times')} className="block text-sm font-medium text-slate-800">Days and times that suit you</label>
                    <input id={id('times')} className={inputCls} value={f.preferred_times} onChange={set('preferred_times')} /></div>
            )}
            <div><label htmlFor={id('msg')} className="block text-sm font-medium text-slate-800">{type === 'question' ? 'Your question' : 'Anything we should know (optional)'}</label>
                <textarea id={id('msg')} rows={3} required={type === 'question'} className={inputCls} value={f.message} onChange={set('message')} /></div>
            {/* Honeypot: hidden from people and screen readers; bots fill it in. */}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px' }}>
                <label htmlFor={id('website')}>Website</label><input id={id('website')} tabIndex={-1} autoComplete="off" value={f.website} onChange={set('website')} />
            </div>
            {state.error && <p className="text-sm text-red-700" role="alert">{state.error}</p>}
            {!compact && <p className="text-xs text-slate-600">Give an email or a phone number so we can reach you. We use it only to answer you.</p>}
            <button type="submit" disabled={state.sending} className="w-full rounded-md bg-slate-900 px-4 py-2 font-semibold text-white hover:bg-slate-900 focus:outline-none focus:ring-4 focus:ring-indigo-300 disabled:opacity-60">
                {state.sending ? 'Sending…' : 'Send'}
            </button>
        </form>
    );
};

const FavouriteButton = ({ listingKey }) => {
    const [on, setOn] = useState(false);
    const [msg, setMsg] = useState('');
    useEffect(() => {
        if (!portalToken()) return;
        portalFetch('/api/client-portal/properties/favorites').then((rows) => setOn(rows.some((r) => r.listing_key === listingKey))).catch(() => {});
    }, [listingKey]);
    if (!portalToken()) {
        return <Link to={`/client-portal/register?next=${encodeURIComponent(window.location.pathname)}`} className="text-sm font-semibold text-indigo-800 underline">Sign up free to save favourites</Link>;
    }
    const toggle = async () => {
        setMsg('');
        try {
            if (on) await portalFetch(`/api/client-portal/properties/favorites/${encodeURIComponent(listingKey)}`, { method: 'DELETE' });
            else await portalFetch('/api/client-portal/properties/favorites', { method: 'POST', body: JSON.stringify({ listing_key: listingKey }) });
            setOn(!on);
        } catch (e) { setMsg(e.message); }
    };
    return (
        <span>
            <button type="button" onClick={toggle} aria-pressed={on}
                className="rounded-md border border-rose-700 px-4 py-2 font-semibold text-rose-800 hover:bg-rose-50 focus:outline-none focus:ring-4 focus:ring-rose-300">
                {on ? '♥ Saved to favourites' : '♡ Save to favourites'}
            </button>
            {msg && <span className="ml-2 text-sm text-red-700" role="alert">{msg}</span>}
        </span>
    );
};

const ShareButton = ({ listing }) => {
    const [msg, setMsg] = useState('');
    const share = async () => {
        const url = window.location.origin + (listing.path || window.location.pathname);
        try {
            if (navigator.share) await navigator.share({ title: addressLine(listing), url });
            else { await navigator.clipboard.writeText(url); setMsg('Link copied'); }
            track('share', listing.key);
        } catch { /* cancelled */ }
    };
    return (
        <span>
            <button type="button" onClick={share}
                className="rounded-md border border-slate-800 px-4 py-2 font-semibold text-indigo-800 hover:bg-indigo-50 focus:outline-none focus:ring-4 focus:ring-indigo-300">
                Share</button>
            {msg && <span className="ml-2 text-sm text-slate-700" role="status">{msg}</span>}
        </span>
    );
};

const PropertyPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    useListingEngagement(data && data.listing ? data.listing.key : null);
    // An owner self-service listing (approved by the office) carries extra facts and its own form.
    const owner = useOwnerListing(data && data.listing ? data.listing.key : null);

    useEffect(() => {
        setData(null);
        setError('');
        fetch(`/api/public/properties/${encodeURIComponent(slug)}`)
            .then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(j.message || 'This property is no longer listed.'); return j; })
            .then((j) => {
                if (j.redirect) { navigate(`/properties/${j.redirect}`, { replace: true }); return; }
                setData(j);
                const l = j.listing;
                document.title = `${addressLine(l)}, ${placeLine(l)} — ${l.price_label} | ${j.identity ? j.identity.brokerage_name : 'Properties'}`;
                if (l.path && l.path !== `/properties/${slug}`) navigate(l.path, { replace: true });
            })
            .catch((e) => setError(e.message));
    }, [slug, navigate]);

    if (error) {
        return (
            <div className="container mx-auto px-4 py-16 text-center sm:px-6">
                <h1 className="text-2xl font-bold text-slate-900">{error}</h1>
                <p className="mt-4"><Link to="/properties" className="font-semibold text-indigo-800 underline">Search all properties</Link></p>
            </div>
        );
    }
    if (!data) return <p className="container mx-auto px-4 py-16 text-slate-700" role="status">Loading…</p>;
    const l = data.listing;
    const commercial = l.type === 'commercial' || l.type === 'industrial';

    return (
        <div className="container mx-auto px-4 py-8 sm:px-6">
            <nav aria-label="Breadcrumb" className="mb-4 text-sm"><Link to="/properties" className="text-indigo-800 underline">← All properties</Link></nav>
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
                <div className="lg:col-span-2">
                    <Gallery l={l} />
                    <header className="mt-6">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className={`rounded px-2 py-0.5 text-sm font-semibold ${STATUS_STYLE[l.status] || ''}`}>{l.status_label}</span>
                            <span className="text-sm font-medium text-slate-700">{l.type_label}{l.type_detail && l.type_detail !== l.type_label ? ` · ${l.type_detail}` : ''}</span>
                        </div>
                        <OwnerListingBadges info={owner} />
                        <h1 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">{addressLine(l)}</h1>
                        <p className="text-lg text-slate-700">{placeLine(l)}{l.county ? ` · ${l.county} County` : ''}</p>
                        <p className="mt-2 text-3xl font-bold text-slate-900">{l.price_label}</p>
                        <div className="mt-3 flex flex-wrap items-center gap-3"><FavouriteButton listingKey={l.key} /><ShareButton listing={l} /></div>
                    </header>

                    <section aria-labelledby="facts-h" className="mt-6">
                        <h2 id="facts-h" className="text-xl font-semibold text-slate-900">Facts</h2>
                        <dl className="mt-2 grid grid-cols-2 gap-x-6 sm:grid-cols-3">
                            <Fact label="Bedrooms" value={l.beds} />
                            <Fact label="Bathrooms" value={l.baths} />
                            <Fact label={commercial ? 'Building size' : 'Living area'} value={l.sqft ? `${Number(l.sqft).toLocaleString('en-US')} sq ft` : null} />
                            <Fact label="Lot" value={l.lot_acres ? `${l.lot_acres} acres` : null} />
                            <Fact label="Year built" value={l.year_built} />
                            <Fact label="Zoning" value={l.zoning} />
                            <Fact label="Cap rate" value={l.cap_rate ? `${l.cap_rate}%` : null} />
                            <Fact label="Lease rate" value={l.lease_rate_basis ? `${l.price_label.split(' ')[0]} ${LEASE_TERMS[l.lease_rate_basis] || ''}` : null} />
                            <Fact label="Lease type" value={l.lease_terms} />
                            <Fact label="MLS #" value={l.mls_number} />
                            <Fact label="Listed" value={l.listed_at ? new Date(l.listed_at).toLocaleDateString('en-US') : null} />
                            {l.close_date && <Fact label="Closed" value={new Date(l.close_date).toLocaleDateString('en-US')} />}
                        </dl>
                    </section>

                    {l.description && (
                        <section aria-labelledby="desc-h" className="mt-6">
                            <h2 id="desc-h" className="text-xl font-semibold text-slate-900">Description</h2>
                            <p className="mt-2 whitespace-pre-line leading-relaxed text-slate-800">{l.description}</p>
                        </section>
                    )}
                    <OwnerListingDetails info={owner} />
                    <Attribution listing={l} className="mt-6 text-sm" />
                    {data.disclaimer && <Disclaimer text={data.disclaimer} />}
                </div>

                <aside className="space-y-6">
                    {l.source === 'own' && !(owner && owner.self_listed) && (
                        <ShowingBooking listingKey={l.key} />
                    )}
                    <section aria-labelledby="inq-h" className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                        <h2 id="inq-h" className="mb-3 text-lg font-semibold text-slate-900">Interested?</h2>
                        {owner && owner.self_listed ? <OwnerInquiryForm listingKey={l.key} info={owner} /> : <InquiryForm listingKey={l.key} />}
                    </section>
                    <IdentityBlock identity={data.identity} />
                    {l.source === 'idx' && data.identity && (
                        <p className="text-xs text-slate-600">This property is listed by another brokerage; {data.identity.brokerage_name} can represent you as a buyer or tenant.</p>
                    )}
                </aside>
            </div>
        </div>
    );
};

export default PropertyPage;
